import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Platform, ScheduleRules, SchedulePreset } from "@marketing/shared";
import { api, unwrap } from "@/lib/api";

export type RuleSource = "page" | "brand" | "default";

export type SchedulePage = {
  id: string;
  platform: Platform;
  displayName: string;
  override: ScheduleRules | null;
  effective: ScheduleRules;
  source: RuleSource;
};

export type BrandSchedule = {
  brand: { id: string; name: string; timezone: string };
  brandRules: ScheduleRules | null;
  defaultRules: ScheduleRules;
  presets: SchedulePreset[];
  pages: SchedulePage[];
};

export type PreviewSlot = { at: string; day: string; localTime: string };

export function useBrandSchedule(brandId: string | null) {
  return useQuery({
    queryKey: ["schedule", brandId],
    queryFn: () => unwrap<BrandSchedule>(api.get(`/schedule/brands/${brandId}`)),
    enabled: !!brandId,
  });
}

export function useSaveSchedule(brandId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { socialAccountId: string | null; rules: ScheduleRules }) =>
      unwrap<{ id: string; created: boolean }>(api.put(`/schedule/brands/${brandId}`, input)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["schedule", brandId] }),
  });
}

export function useClearOverride(brandId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (socialAccountId: string) =>
      api.delete(`/schedule/brands/${brandId}/pages/${socialAccountId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["schedule", brandId] }),
  });
}

/**
 * Preview runs against the UNSAVED form state, so the owner can see real times
 * before committing — the same reason the voice editor previews a sample post.
 */
export function usePreviewSchedule(brandId: string | null) {
  return useMutation({
    mutationFn: (input: { rules: ScheduleRules; platform: Platform; days: number }) =>
      unwrap<PreviewSlot[]>(api.post(`/schedule/brands/${brandId}/preview`, input)),
  });
}

/**
 * Whether anything is publishing right now.
 *
 * Polled rather than fetched once: the failure it reports — the worker process
 * stopping — happens while the page is open, which is exactly when a one-shot
 * query would already have answered "fine". A minute matches the sweeper's own
 * cadence, so the banner appears about one sweep after publishing stops.
 */
export type PublisherStatus = {
  running: boolean;
  lastSweepAt: string | null;
  staleForMs: number | null;
  reason: "ok" | "never_started" | "stopped" | "unreachable";
  overdue: number;
  oldestOverdueAt: string | null;
};

export function usePublisherStatus() {
  return useQuery({
    queryKey: ["publisher-status"],
    queryFn: () => unwrap<PublisherStatus>(api.get("/schedule/publisher")),
    refetchInterval: 60_000,
    // The banner is not worth a red error state of its own: if this request
    // fails the page still works, and the next poll will say so.
    retry: false,
  });
}
